import type { ReactNode } from "react";
import { twJoin } from "tailwind-merge";

import { ContentBlock, type ContentBlockWidth } from "./ContentBlock";
import type { DesignSystemElementProps, DesignSystemRef } from "./contracts/dom";

export type PageProps = {
  title?: string;
  breadcrumbs: ReactNode;
  actions?: ReactNode;
  header?: ReactNode;
  children?: ReactNode;
  contentSize?: Exclude<ContentBlockWidth, "full">;
  containerBoundary?: "self" | "inherit";
} & Omit<DesignSystemElementProps<"section">, "children"> &
  DesignSystemRef<HTMLElement>;

export function Page({
  title,
  children,
  breadcrumbs,
  actions,
  header,
  contentSize,
  containerBoundary = "self",
  ref,
  ...nativeProps
}: PageProps): ReactNode {
  return (
    <section
      {...nativeProps}
      ref={ref}
      className={twJoin(
        containerBoundary === "self" && "@container",
        "relative mx-auto flex min-h-screen max-w-content-full flex-col gap-space-md p-space-md pb-page-bottom *:last:flex *:last:flex-1 *:last:flex-col",
      )}
    >
      {(title || breadcrumbs || actions) && (
        <header className="flex flex-wrap items-center justify-between gap-space-md">
          {(breadcrumbs || actions) && (
            <div className="flex min-h-control-md w-full flex-wrap items-center gap-x-page-header-group gap-y-space-md">
              {breadcrumbs && <div className="inline-flex items-center">{breadcrumbs}</div>}
              {actions && <div className="ml-auto flex flex-wrap gap-space-sm">{actions}</div>}
            </div>
          )}
          {title && (
            <h1 className="block max-w-full text-heading-2 text-text-brand-secondary @md:text-heading-1">{title}</h1>
          )}
        </header>
      )}

      {header}
      <ContentBlock width={contentSize}>{children}</ContentBlock>
    </section>
  );
}
